import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Alert,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export const StepMetadatos = ({ state, dispatch }) => {
  // For metadata we work with all contenidos when no OCR, or only success contenidos when OCR ran
  const activeContenidos = (state.useOcr && (state.id_ocr_plantilla || state.regiones?.length > 0))
    ? state.contenidos.filter((c) => c.status === "success")
    : state.contenidos;

  const metadatos = state.metadatosDefinition || [];

  const hasOcrDataForField = (id_metadatos) => {
    return activeContenidos.some((c) => {
      const val = state.contenidoMetadata?.[c.contenidoId]?.[id_metadatos];
      return val && val !== "";
    });
  };

  const getFieldMode = (id_metadatos) => {
    return state.fieldModes?.[id_metadatos] || "manual";
  };

  const handleModeChange = (_event, id_metadatos, newMode) => {
    if (!newMode) return;
    dispatch({ type: "SET_FIELD_MODE", payload: { id_metadatos, mode: newMode } });
  };

  const handleManualChange = (id_metadatos, value) => {
    dispatch({ type: "SET_MANUAL_VALUE", payload: { id_metadatos, value } });
  };

  const handleContenidoMetadataChange = (contenidoId, id_metadatos, value) => {
    dispatch({ type: "SET_CONTENIDO_METADATA_VALUE", payload: { contenidoId, id_metadatos, value } });
  };

  if (metadatos.length === 0) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: "auto" }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0A395F", mb: 0.5 }}>
            Metadatos
          </Typography>
        </Box>
        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ borderRadius: 2 }}>
          El tipo de contenido seleccionado no tiene metadatos definidos. Puedes continuar al siguiente paso.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Step header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#0A395F", mb: 0.5 }}>
          Metadatos
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configura los valores por campo para los {activeContenidos.length} contenido{activeContenidos.length !== 1 ? "s" : ""} a crear.
        </Typography>
      </Box>

      {/* Legend */}
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon />}
        sx={{ mb: 3, borderRadius: 2, backgroundColor: "#e3f2fd", "& .MuiAlert-icon": { color: "#1565C0" } }}
      >
        {state.useOcr ? (
          <>
            <strong>Modo Manual:</strong> aplica el mismo valor a todos los contenidos.{" "}
            <strong>Modo OCR:</strong> permite editar el valor extraido individualmente por contenido.
          </>
        ) : (
          <>Completa los valores de cada campo para cada contenido de forma individual.</>
        )}
      </Alert>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2, overflowX: "auto", border: "1px solid #e0e7ef" }}
      >
        <Table size="small" sx={{ minWidth: 500 }}>
          <TableHead>
            <TableRow
              sx={{
                background: "linear-gradient(135deg, #0A395F 0%, #1565C0 100%)",
              }}
            >
              <TableCell
                sx={{
                  fontWeight: 700,
                  minWidth: 160,
                  color: "#fff",
                  fontSize: "0.8rem",
                  py: 1.5,
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                  background: "linear-gradient(135deg, #0A395F 0%, #1565C0 100%)",
                }}
              >
                Campo
              </TableCell>
              {state.useOcr && (
                <TableCell sx={{ fontWeight: 700, minWidth: 140, color: "#fff", fontSize: "0.8rem", py: 1.5 }}>
                  Modo
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: 700, color: "#fff", fontSize: "0.8rem", py: 1.5 }}>
                Valor(es)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {metadatos.map((meta, rowIndex) => {
              const id_meta = meta.id;
              const mode = getFieldMode(id_meta);
              const ocrAvailable = (state.useOcr && (state.id_ocr_plantilla || state.regiones?.length > 0)) && hasOcrDataForField(id_meta);
              const manualValue = state.manualValues?.[id_meta] || "";
              const isOcrMode = mode === "ocr";
              // When OCR is disabled, always show individual per-contenido fields
              const showIndividual = !state.useOcr || isOcrMode;

              return (
                <TableRow
                  key={id_meta}
                  sx={{
                    verticalAlign: "top",
                    backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f7f9fc",
                    "&:hover": { backgroundColor: "#eef4ff" },
                    transition: "background-color 0.15s",
                  }}
                >
                  {/* Field name */}
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f7f9fc",
                      py: 1.5,
                      borderRight: "1px solid #e0e7ef",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#0A395F" }}>
                      {meta.label_form}
                    </Typography>
                    {meta.label_grilla && meta.label_grilla !== meta.label_form && (
                      <Typography variant="caption" color="text.secondary">
                        ({meta.label_grilla})
                      </Typography>
                    )}
                    {ocrAvailable && (
                      <Chip
                        icon={<DocumentScannerIcon style={{ fontSize: 10 }} />}
                        label="OCR"
                        size="small"
                        sx={{
                          mt: 0.5,
                          fontSize: 9,
                          height: 16,
                          backgroundColor: "#e8f5e9",
                          color: "#2e7d32",
                          display: "flex",
                          width: "fit-content",
                        }}
                      />
                    )}
                  </TableCell>

                  {/* Mode toggle — only when OCR is active */}
                  {state.useOcr && (
                    <TableCell sx={{ py: 1.5 }}>
                      <Tooltip
                        title={
                          !ocrAvailable && (state.id_ocr_plantilla || state.regiones?.length > 0)
                            ? "Sin valores OCR para este campo"
                            : ""
                        }
                      >
                        <span>
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={mode}
                            onChange={(e, val) => handleModeChange(e, id_meta, val)}
                            sx={{
                              "& .MuiToggleButton-root": {
                                fontSize: 11,
                                px: 1.25,
                                py: 0.5,
                                lineHeight: 1.4,
                                borderColor: "#c5cae9",
                              },
                              "& .MuiToggleButton-root.Mui-selected": {
                                backgroundColor: isOcrMode ? "#e8f5e9" : "#e3f2fd",
                                color: isOcrMode ? "#2e7d32" : "#1565C0",
                                fontWeight: 700,
                              },
                            }}
                          >
                            <ToggleButton value="manual">
                              <EditIcon sx={{ fontSize: 12, mr: 0.5 }} />
                              Manual
                            </ToggleButton>
                            <ToggleButton value="ocr" disabled={!ocrAvailable}>
                              <DocumentScannerIcon sx={{ fontSize: 12, mr: 0.5 }} />
                              OCR
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}

                  {/* Value(s) */}
                  <TableCell sx={{ py: 1.5 }}>
                    {!showIndividual ? (
                      <TextField
                        size="small"
                        value={manualValue}
                        onChange={(e) => handleManualChange(id_meta, e.target.value)}
                        placeholder="Valor para todos los contenidos"
                        sx={{
                          minWidth: 240,
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#f8fbff",
                            "&:hover": { backgroundColor: "#eef4ff" },
                          },
                        }}
                        fullWidth
                      />
                    ) : (
                      <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 0.5 }}>
                        {activeContenidos.map((contenido, idx) => {
                          const val = state.contenidoMetadata?.[contenido.contenidoId]?.[id_meta] || "";
                          const failed = contenido.status === "error";
                          if (failed) {
                            return (
                              <Tooltip key={contenido.contenidoId} title="OCR fallido para este contenido">
                                <Box
                                  sx={{
                                    minWidth: 90,
                                    p: 0.75,
                                    borderRadius: 1,
                                    backgroundColor: "#fff5f5",
                                    border: "1px solid #ffcdd2",
                                  }}
                                >
                                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: 600, color: "#d32f2f", fontSize: 10 }}>
                                    #{idx + 1}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "#ef9a9a", fontSize: 10 }}>—</Typography>
                                </Box>
                              </Tooltip>
                            );
                          }
                          return (
                            <Box key={contenido.contenidoId} sx={{ minWidth: 110 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mb: 0.5,
                                  fontWeight: 700,
                                  color: "#0A395F",
                                  fontSize: 10,
                                }}
                              >
                                #{idx + 1}
                              </Typography>
                              <TextField
                                size="small"
                                value={val}
                                onChange={(e) =>
                                  handleContenidoMetadataChange(contenido.contenidoId, id_meta, e.target.value)
                                }
                                inputProps={{ style: { fontSize: 12 } }}
                                sx={{
                                  minWidth: 100,
                                  "& .MuiOutlinedInput-root": {
                                    backgroundColor: "#f1f8f1",
                                    "& fieldset": { borderColor: "#a5d6a7" },
                                    "&:hover fieldset": { borderColor: "#2e7d32" },
                                  },
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
